import { IApiClient } from "@common/network/interfaces";
import { ApiClient } from "@network/client";
import {
  IFetchPaymentDataAction,
  IFetchPaymentPlanPayload,
  IMerchantDetails,
  IPaymentInit,
  IPaymentInitiation,
  IRefundAmountPayload,
} from "@common/redux/payment/interface";
import { getConfig } from "@config/index";

import {
  addMerchant,
  getMerchant,
  getMerchantById,
  refundAmountPath,
  subscriptions,
} from "../apis/urls";
import { currencyCode } from "@presentation/FormBuilder/components/FormFieldSelector/interfaces";
import { ProductName } from "module/interfaces/formInterface";

const ENDPOINTS = {
  addMerchant: () => addMerchant,
  refundAmount: () => refundAmountPath,
  getMerchantDetails: () => getMerchant,
  getMerchantDetailsById: () => getMerchantById,
  getMerchantDetailsByIdPublic: () => `api/payment-service/public/payment/merchantDetails/getAllDataByPaymentId`,
  submitPayment: (paymentMode) =>
    `api/payment-service/public/payment/${paymentMode.toLowerCase()}/submitPaymentDetail`,
  fetchPlanData: (product: ProductName, currencyValue: currencyCode) =>
    `api/payment-service/plans/product/${product}/currency/${currencyValue}`,
  paymentUrl: (
    userId: string,
    contact: string,
    name: string,
    email: string,
    planId: string,
    callback_url: string
  ) =>
    `api/payment-service/subscriptions?userId=${userId}&contact=${contact}&name=${name}&email=${email}&planId=${planId}&callback_url=${callback_url}`,

  verifySubscription: () => subscriptions,
};
class FormsRepository {
  private apiClient: IApiClient;
  constructor(apiClient: IApiClient) {
    this.apiClient = apiClient;
  }

  public addMerchantDetails = async (merchantDetails: IMerchantDetails) => {
    const payload = merchantDetails;
    const result = await this.apiClient.post(
      ENDPOINTS.addMerchant(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public fetchPaymentPlans = async (actionPayload: IFetchPaymentDataAction) => {
    const actionData = actionPayload?.payload;
    const product = actionData?.product ?? ProductName?.forms;
    const currency = actionData?.curreny ?? currencyCode?.USA;
    const result = await this.apiClient.get(
      ENDPOINTS.fetchPlanData(product, currency),
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public refundAmount = async (refundData: IRefundAmountPayload) => {
    const submissionId = refundData.submissionId;
    const refundAmt = refundData.refundAmt;
    const result = await this.apiClient.post(
      ENDPOINTS.refundAmount(),
      {},
      { submissionId, refundAmt },
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public paymentInitiation = async (paymentData: IPaymentInitiation) => {
    const userId = paymentData?.userId;
    const contact = paymentData?.contact;
    const name = paymentData?.name;
    const planId = paymentData?.planId;
    const email = paymentData?.email;
    const callback_url = paymentData?.callback_url;
    const url = ENDPOINTS.paymentUrl(
      userId,
      contact,
      name,
      email,
      planId,
      callback_url
    );
    const result = await this.apiClient.post(
      url,
      {},
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public getMerchantDetails = async (userId: string) => {
    const result = await this.apiClient.get(
      ENDPOINTS.getMerchantDetails() + userId,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public submitPayment = async ({ paymentData, paymentMode }) => {
    const payload = paymentData;
    let url = ENDPOINTS.submitPayment(paymentMode);
    const result = await this.apiClient.post(
      url,
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public getMerchantDetailsById = async (
    paymentIdsString: string,
    isAnonymousSubAllowed: boolean
  ) => {
    const isPublic = isAnonymousSubAllowed;
    const url = isPublic
      ? `${ENDPOINTS.getMerchantDetailsByIdPublic()}?paymentId=${paymentIdsString}`
      : ENDPOINTS.getMerchantDetailsById() + `?paymentId=${paymentIdsString}`;
    const result = await this.apiClient.get(
      url,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };
  public getSubscriptionStatus = async (subscriptionId: string) => {
    const url =
      ENDPOINTS.verifySubscription() + `/${subscriptionId}`;
    const result = await this.apiClient.get(
      url,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };
}

const paymentRepository = new FormsRepository(ApiClient);

export { paymentRepository as PaymentRepository };
